import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';

import { Logo } from '@/shared/ui';

import { LanguageSelect } from '../components/LanguageSelect';
import { ThemeToggle } from '../components/ThemeToggle';

import { RouteFallback } from './RouteFallback';

/**
 * Chromeless shell for unauthenticated screens — `Log_in_1` / `Log_in_2`.
 *
 * The reference is a tall brand-gradient panel with the logo centred in its
 * lower third, rounded at the bottom, and the form sitting flat on the page
 * background beneath it. Not a card overlapping the band, which is what the
 * previous build drew.
 *
 * THE LOGO IS NOVIX. The mockup renders MWRLife's shield-and-script wordmark
 * because it was drawn for that product; this is a different one. The mark is
 * `design/logo/novix-mark-c.svg` and the wordmark is set in Onest SemiBold,
 * both in `--on-gradient` white, which is what the gradient calls for.
 *
 * The theme and language toggles stay on this surface even though the mockup
 * has none: they moved off the app header onto Profile, which an
 * unauthenticated visitor cannot reach, and a user who cannot read the
 * interface cannot sign in to go and fix it.
 */
export function AuthLayout() {
  const { t } = useTranslation();

  return (
    <div className="min-h-dvh bg-[var(--bg-muted)]">
      <div className="mx-auto flex min-h-dvh w-full max-w-[var(--app-max-width)] flex-col bg-[var(--bg-muted)] md:shadow-[0_0_0_1px_var(--border)]">
        <div className="brand-gradient flex min-h-[38dvh] shrink-0 flex-col rounded-b-2xl px-5 pt-[var(--safe-top)] pb-10 text-[var(--on-gradient)]">
          <div className="flex items-center justify-end gap-1 pt-3 [&_button:hover]:bg-white/20 [&_button:hover]:text-[var(--on-gradient)] [&_button]:text-[var(--on-gradient)]">
            <LanguageSelect tone="gradient" />
            <ThemeToggle />
          </div>

          <div className="flex flex-1 items-end justify-center">
            {/*
              One accessible name for the lockup, not two: the wordmark inside
              Logo is aria-hidden, so the mark carries the label.
            */}
            <Logo size={56} variant="full" tone="on-gradient" title={t('common.appName')} />
          </div>
        </div>

        <main id="main" className="flex-1 px-5 pt-7 pb-10">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
